import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Billable item categories matching backend schema (services/project-tracker/backend/schemas/billable_items.py:12-18)
// Phase 7 Batch 2: Added internal_work category
// QW-3: Added renewal category (Christa feedback 2024-12-22)
export type BillableItemCategory =
  | 'professional_fee'
  | 'add_service'
  | 'renewal'
  | 'billable_expense'
  | 'pass_through'
  | 'internal_work';

export interface CategoryOption {
  value: BillableItemCategory;
  label: string;
  description: string;
  taxInfo: string;
}

/**
 * The one place a billable item category's NAME, meaning and tax consequence
 * are written down. Exported (QA round 2 on SPEC-v017-completion-2026-09-27
 * R4) so the invoice Add Item modal's per-line control carries the same
 * words instead of five bare labels of its own — christa-reviewer HIGH 3:
 * our 2026-09-24 email told her to use Pass-Through for application fees,
 * which her own books contradict (billable_expense 379 uses, pass_through 26
 * and all 26 consultant scope items), and nothing on the page said otherwise.
 */
export const CATEGORY_OPTIONS: readonly CategoryOption[] = [
  {
    value: 'professional_fee',
    label: 'Professional Fee',
    description: 'Original contract work, revenue',
    taxInfo: '5% GST',
  },
  {
    value: 'add_service',
    label: 'Add Service',
    description: 'Additional work beyond scope, revenue',
    taxInfo: '5% GST',
  },
  {
    value: 'renewal',
    label: 'Renewal',
    description: 'License/permit renewal work, revenue',
    taxInfo: '5% GST',
  },
  {
    value: 'billable_expense',
    label: 'Billable Expense',
    description: 'Reimbursable expenses, not revenue',
    taxInfo: 'No tax',
  },
  {
    value: 'pass_through',
    label: 'Pass-Through',
    description: 'Consultant fees billed on behalf, not revenue',
    taxInfo: 'No tax',
  },
  {
    value: 'internal_work',
    label: 'Internal Work',
    description: 'Internal tasks (always $0), not revenue',
    taxInfo: 'No tax',
  },
];

interface CategorySelectProps {
  value: BillableItemCategory | undefined;
  onChange: (value: BillableItemCategory) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  workflowStage?: string; // Phase 7.5.3: Workflow-dependent fee type restrictions
  isTemplate?: boolean; // Phase 7.5.3: Templates can use all categories
}

/**
 * Phase 7.5.3: Determine which categories are disabled based on workflow stage
 * Business rules:
 * - proposal: professional_fee enabled, add_service disabled
 * - other stages: add_service enabled, professional_fee disabled
 * - templates: all categories enabled
 * - other categories (billable_expense, pass_through, internal_work) always enabled
 *
 * Exported (fix/v017-addmodal-profee) so every other create-a-billable-item
 * surface — AddItemModal's "Create New Billable Item" included — derives the
 * same restriction from one place instead of re-implementing the workflow
 * stage check and drifting from it.
 */
export function getCategoryDisabledReason(
  category: BillableItemCategory,
  workflowStage?: string,
  isTemplate?: boolean
): string | null {
  // Templates can use all categories
  if (isTemplate) return null;

  // No workflow stage means no restrictions
  if (!workflowStage) return null;

  // Phase 7.5.13: Check for all proposal stages
  const isProposal = ['proposal_draft', 'proposal_sent', 'proposal_declined'].includes(workflowStage);

  if (category === 'professional_fee' && !isProposal) {
    return 'Professional fees are set at the proposal stage. Add extra work on a project that is under way as an Additional Service.';
  }

  if (category === 'add_service' && isProposal) {
    return 'Add services can only be added after proposal is accepted';
  }

  return null;
}

export function CategorySelect({
  value,
  onChange,
  disabled = false,
  className,
  id,
  workflowStage,
  isTemplate,
}: CategorySelectProps) {
  // Phase 7.5.3: Determine which categories are restricted
  // Hide internal_work unless the current item already has it (deprecated category)
  const visibleCategories = CATEGORY_OPTIONS.filter(
    c => c.value !== 'internal_work' || value === 'internal_work'
  );
  const restrictedCategories = visibleCategories.map(category => ({
    ...category,
    disabledReason: getCategoryDisabledReason(category.value, workflowStage, isTemplate),
  }));

  // Check if current value is restricted (for helper text)
  const hasRestrictions = restrictedCategories.some(c => c.disabledReason !== null);

  return (
    <div className="space-y-1">
      <Select
        value={value}
        onValueChange={onChange}
        disabled={disabled}
      >
        <SelectTrigger type="button" className={className} id={id}>
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        <SelectContent>
          {restrictedCategories.map((category) => (
            <SelectItem
              key={category.value}
              value={category.value}
              disabled={category.disabledReason !== null}
              title={category.disabledReason || undefined}
            >
              <div className="flex flex-col">
                <span className={`font-medium text-left ${category.disabledReason ? 'text-muted-foreground' : ''}`}>
                  {category.label}
                  {category.disabledReason && ' (restricted)'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {category.description} ({category.taxInfo})
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Phase 7.5.3: Helper text explaining restrictions */}
      {hasRestrictions && workflowStage && !isTemplate && (
        <p className="text-xs text-muted-foreground">
          {['proposal_draft', 'proposal_sent', 'proposal_declined'].includes(workflowStage)
            ? 'During proposals, use Professional Fees for contract scope. Add Services become available after acceptance.'
            : 'For active projects, use Add Services for additional work. Professional Fees are set during proposals.'}
        </p>
      )}
    </div>
  );
}
